/**
 * ActionOverflow — universal "More ▾" dropdown for record-page and section
 * headers (Push #28 rule; cap tightened Push 4-1af 2026-07-10).
 *
 * Rule: any record-page or section header shows AT MOST 2 inline action
 * buttons (per Ean Push 4-1af — was 3). The 3rd + remaining collapse into
 * this overflow menu so the header stays scannable.
 *
 * Action priority order (decided by the caller, not this component):
 *   1. Primary action (filled teal) — always inline, always rightmost
 *   2. Secondary action #1 (outlined) — inline if room
 *   3+. Everything else → overflow
 *
 * Back-nav links ("← Connectors") are a separate concern and don't count
 * against the 3-action limit. They render at the far-left of the header
 * strip, not in the action group.
 *
 * Usage:
 *   <div className="record-actions">
 *     <button className="btn btn-secondary">Clone</button>      // inline #2
 *     <button className="btn btn-primary">Use in chat</button>  // primary
 *     <ActionOverflow items={[
 *       { label: 'Copy prompt', onClick: handleCopy },
 *       { label: 'Export JSON', onClick: handleExport },
 *     ]} />
 *   </div>
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface ActionOverflowItem {
  label: string;
  onClick: () => void;
  /** Optional leading element (icon, etc.). Skipped per Push #16 icon
   *  discipline unless icons are already in the surrounding visual lang. */
  icon?: ReactNode;
  /** Destructive ops render in danger color. */
  danger?: boolean;
  /** Greyed out + non-clickable. Tooltip explains why via `title`. */
  disabled?: boolean;
  /** Aria/native title for hover hint. */
  title?: string;
}

interface ActionOverflowProps {
  items: ActionOverflowItem[];
  /** Override the trigger label. Default "More ▾". */
  triggerLabel?: string;
  /** Trigger button size matches surrounding action buttons. */
  size?: 'sm' | 'md';
}

export function ActionOverflow({
  items,
  triggerLabel = 'More',
  size = 'md',
}: ActionOverflowProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e: MouseEvent) {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={rootRef} className="action-overflow">
      {/* Push 4-1t (2026-07-09): icon-only chevron trigger per Ean nitpick —
         "More" text was redundant when a caret already conveys expand.
         Same compact envelope as sibling record-detail buttons; role="button"
         + aria-label='More actions' + aria-haspopup preserve accessibility. */}
      <button
        type="button"
        className={`btn btn-secondary action-overflow-trigger${size === 'sm' ? ' btn-sm' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel === 'More' ? 'More actions' : triggerLabel}
        title={triggerLabel === 'More' ? 'More actions' : triggerLabel}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          style={{
            transition: 'transform 120ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="action-overflow-menu" role="menu">
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => {
                if (item.disabled === true) return;
                item.onClick();
                setOpen(false);
              }}
              disabled={item.disabled === true}
              title={item.title ?? ''}
              className={`action-overflow-item${item.danger === true ? ' is-danger' : ''}`}
            >
              {item.icon !== undefined && (
                <span className="action-overflow-icon" aria-hidden="true">{item.icon}</span>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
